import React, { useEffect, useState } from "react";
import { authHeaders } from "../api/auth";

type TeacherDashboardData = {
  student_counts: {
    total_students: number;
    engaged: number;
    monitor: number;
    attention_required: number;
  };
  welfare_overview: {
    green: number;
    amber: number;
    red: number;
    white: number;
  };
  students_requiring_attention: {
    id: string;
    student_id: string;
    student_name?: string | null;
    event_type: string;
    severity: string;
    source: string;
    summary: string;
    evidence?: string | null;
    status: string;
    first_seen_at: string;
    created_at: string;
  }[];
  positive_notices: {
    id: string;
    student_id: string;
    event_type: string;
    severity: string;
    summary: string;
    created_at: string;
  }[];
  recent_activity: {
    notes: any[];
    actions: any[];
  };
};

type Student = {
  id: string;
  email: string;
  display_name?: string | null;
  role: string;
  is_active: boolean;
  teacher_id?: string | null;
  student_limit?: number | null;
  last_activity?: string | null;
  project_count?: number;
  progress_percent?: number;
  energy_trend?: string | null;
};

export default function TeacherDashboard({ onLogout }: { onLogout: () => void }) {
  const [students, setStudents] = useState<Student[]>([]);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("Student123!");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<TeacherDashboardData | null>(null);

  async function loadDashboard() {
  try {
    const res = await fetch("/welfare/dashboard", {
      headers: authHeaders(),
    });

    if (!res.ok) throw new Error("Could not load dashboard");

    const data = await res.json();
    setDashboard(data);
  } catch (err) {
    console.error(err);
  }
}
  
  async function loadStudents() {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/users/students", {
        headers: authHeaders(),
      });

      if (!res.ok) throw new Error("Could not load students");

      const data = await res.json();
      setStudents(data);
    } catch (err) {
      console.error(err);
      setError("Could not load students.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
  void loadStudents();
  void loadDashboard();
}, []);
  
  async function createStudent(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    try {
      const res = await fetch("/users/students", {
        method: "POST",
        headers: {
          ...authHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          display_name: displayName,
          email,
          password,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Could not create student");
      }

      setDisplayName("");
      setEmail("");
      setPassword("Student123!");
      await loadStudents();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Could not create student.");
    }
  }

  async function deactivateStudent(studentId: string) {
    setError(null);

    try {
      const res = await fetch(`/users/students/${studentId}/deactivate`, {
        method: "PATCH",
        headers: authHeaders(),
      });

      if (!res.ok) throw new Error("Could not deactivate student");

      await loadStudents();
    } catch (err) {
      console.error(err);
      setError("Could not deactivate student.");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#050608", color: "#e5e5e5" }}>
      <header
        style={{
          padding: "1rem 1.5rem",
          borderBottom: "1px solid #e5e7eb",
          background: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#111827",
        }}
      >
        <div>
          <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>Teacher Dashboard</div>
          <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>
            Manage student accounts
          </div>
        </div>

        <button
          type="button"
          onClick={onLogout}
          style={{
            padding: "0.45rem 0.8rem",
            borderRadius: 999,
            border: "1px solid #9ca3af",
            color: "#374151",
            background: "transparent",
            cursor: "pointer",
          }}
        >
          Log out
        </button>
      </header>

      <main style={{ padding: "1.5rem", maxWidth: 900, margin: "0 auto" }}>

      {dashboard && (
  <section
    style={{
      padding: "1rem",
      borderRadius: 12,
      border: "1px solid #222",
      background: "#111",
      marginBottom: "1.5rem",
    }}
  >
    <h2 style={{ marginTop: 0 }}>Today</h2>

    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
      <strong>{dashboard.student_counts.total_students} Students</strong>
      <strong>{dashboard.student_counts.monitor} Monitor</strong>
      <strong>{dashboard.student_counts.attention_required} Review</strong>
    </div>

    <h3>Students Requiring Attention</h3>

    {dashboard.students_requiring_attention.length === 0 ? (
      <p style={{ opacity: 0.75 }}>No students currently require review.</p>
    ) : (
      <div style={{ display: "grid", gap: "0.6rem" }}>
        {dashboard.students_requiring_attention.map((item) => (
          <div
            key={item.id}
            style={{
              padding: "0.75rem",
              borderRadius: 10,
              border: "1px solid #333",
              background: "#181818",
            }}
          >
            <div style={{ fontWeight: 700 }}>
  {item.student_name || "Unknown student"}
            </div>

            <div style={{ marginTop: "0.25rem" }}>
              <div
                style={{
                  display: "inline-block",
                  padding: "0.2rem 0.5rem",
                  borderRadius: 999,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  marginBottom: "0.4rem",
                  background:
                    item.severity === "RED"
                      ? "#7f1d1d"
                      : item.severity === "AMBER"
                      ? "#78350f"
                      : item.severity === "GREEN"
                      ? "#14532d"
                      : "#374151",
                  color: "#ffffff",
                }}
              >
                {item.severity}
              </div>

              <div>
                {item.summary}
              </div>
            </div>

            {item.evidence && (
              <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>
                {item.evidence}
              </div>
            )}
          </div>
        ))}
      </div>
    )}

<h3 style={{ marginTop: "1.2rem" }}>Positive Activity</h3>

  {dashboard.positive_notices.length === 0 ? (
    <p style={{ opacity: 0.75 }}>No positive notices recorded yet.</p>
  ) : (
    <div style={{ display: "grid", gap: "0.6rem" }}>
      {dashboard.positive_notices.map((notice) => (
        <div
          key={notice.id}
          style={{
            padding: "0.75rem",
            borderRadius: 10,
            border: "1px solid #333",
            background: "#181818",
          }}
        >
          <div style={{ fontWeight: 700 }}>
            {notice.summary}
          </div>
        </div>
      ))}
    </div>
  )}

    <h3 style={{ marginTop: "1.2rem" }}>Welfare Overview</h3>

    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
      <span>Green: {dashboard.welfare_overview.green}</span>
      <span>Amber: {dashboard.welfare_overview.amber}</span>
      <span>Red: {dashboard.welfare_overview.red}</span>
      <span>White: {dashboard.welfare_overview.white}</span>
    </div>
  </section>
)}

        <section
          style={{
            padding: "1rem",
            borderRadius: 12,
            border: "1px solid #222",
            background: "#111",
            marginBottom: "1.5rem",
          }}
        >
          <h2 style={{ marginTop: 0 }}>Create student</h2>

          <form onSubmit={createStudent} style={{ display: "grid", gap: "0.75rem" }}>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Student name"
              required
              style={inputStyle}
            />

            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Student email"
              type="email"
              required
              style={inputStyle}
            />

            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Temporary password"
              type="text"
              required
              style={inputStyle}
            />

            <button type="submit" style={primaryButtonStyle}>
              Create student
            </button>
          </form>
        </section>

        <section
          style={{
            padding: "1rem",
            borderRadius: 12,
            border: "1px solid #222",
            background: "#111",
          }}
        >
          <h2 style={{ marginTop: 0 }}>Students</h2>

          {loading && <p>Loading students…</p>}
          {error && <p style={{ color: "#fca5a5" }}>{error}</p>}

          {!loading && students.length === 0 && (
            <p style={{ opacity: 0.8 }}>No students created yet.</p>
          )}

          <div style={{ display: "grid", gap: "0.75rem" }}>
            {students.map((student) => (
              <div
                key={student.id}
                style={{
                  padding: "0.9rem",
                  borderRadius: 10,
                  border: "1px solid #333",
                  background: "#181818",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {student.display_name || student.email.split("@")[0]}
                    </div>
                  <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>
                    {student.email}
                    </div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.65 }}>
                    {student.is_active ? "Active" : "Inactive"}
                    </div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.65 }}>
                    Last activity: {student.last_activity ? new Date(student.last_activity).toLocaleString() : "No activity yet"}
                    </div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.65 }}>
                    Projects: {student.project_count ?? 0}
                    </div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.65 }}>
                    Progress: {student.progress_percent ?? 0}%
                    </div>
                  <div
                        style={{
                            fontSize: "0.8rem",
                            opacity: 0.9,
                            color:
                        student.energy_trend === "improving"
                            ? "#22c55e"
                            : student.energy_trend === "low"
                            ? "#ef4444"
                            : student.energy_trend === "steady"
                            ? "#eab308"
                            : "#9ca3af", // grey for no data
                        }}
                        >
                        Energy: {student.energy_trend ?? "No data"}
                        </div>

                </div>

                {student.is_active && (
                  <button
                    type="button"
                    onClick={() => deactivateStudent(student.id)}
                    style={dangerButtonStyle}
                  >
                    Deactivate
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "0.65rem 0.8rem",
  borderRadius: 8,
  border: "1px solid #374151",
  background: "#050608",
  color: "#e5e5e5",
  fontSize: "1rem",
};

const primaryButtonStyle: React.CSSProperties = {
  padding: "0.7rem 1rem",
  borderRadius: 999,
  border: "none",
  background: "#0991ED",
  color: "white",
  cursor: "pointer",
  fontSize: "1rem",
};

const dangerButtonStyle: React.CSSProperties = {
  padding: "0.5rem 0.8rem",
  borderRadius: 999,
  border: "1px solid #ef4444",
  background: "transparent",
  color: "#fca5a5",
  cursor: "pointer",
};